import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  AlertActionLink,
  Breadcrumb,
  BreadcrumbItem,
  Bullseye,
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  Label,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Spinner,
  Stack,
  StackItem,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, NetworkIcon, PencilAltIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { useNetwork } from '../../../hooks/useNetwork';
import { useNetworkAddresses } from '../../../hooks/useNetworkAddresses';
import { useNetworks } from '../../../hooks/useNetworks';
import type { Network, NetworkAddress } from '../../../types/models';
import NetworkActionComingSoonModal from './NetworkActionComingSoonModal';
import { buildNetworkTree } from './networkTree';
import { renderDescription, renderFamilyLabel, renderStatusLabel, renderVlan } from './networkLabels';

const renderSite = (site: Network['site']): React.ReactNode => {
  if (site === null) return '—';
  if (typeof site === 'number') return `Site #${site}`;
  return site.name ?? `Site #${site.id}`;
};

const renderAddressStatusLabel = (status: string | null): React.ReactNode => {
  if (status === null) return '—';
  switch (status) {
    case 'active':
      return <Label color="green">{status}</Label>;
    case 'reserved':
      return <Label color="orange">{status}</Label>;
    case 'deprecated':
      return <Label color="grey">{status}</Label>;
    case 'dhcp':
      return <Label color="blue">{status}</Label>;
    case 'slaac':
      return <Label color="purple">{status}</Label>;
    default:
      return <Label>{status}</Label>;
  }
};

const renderAddressRole = (role: string | null): React.ReactNode => {
  if (role === null) return '—';
  return <Label color="grey">{role}</Label>;
};

const renderPlainText = (value: string): React.ReactNode => (value === '' ? '—' : value);

const renderAddressAssigned = (assigned: NetworkAddress['assigned']): React.ReactNode => {
  if (assigned === null) return '—';
  if (assigned.display !== null && assigned.display !== '') {
    return assigned.display;
  }
  if (assigned.type !== null && assigned.id !== null) {
    return `${assigned.type} #${assigned.id}`;
  }
  return '—';
};

function NetworkDetail() {
  const auth = useAuth();
  const location = useLocation();
  const { networkId: networkIdParam } = useParams<{ networkId: string }>();
  const parsedId = Number(networkIdParam);
  const idIsValid = Number.isInteger(parsedId) && parsedId > 0;

  const { network, loading, error, refetch } = useNetwork(idIsValid ? parsedId : 0);
  const {
    addresses,
    loading: addressesLoading,
    error: addressesError,
    refetch: refetchAddresses,
  } = useNetworkAddresses(idIsValid ? parsedId : 0, {
    skip: network === null || network.children > 0,
  });
  const {
    networks: allNetworks,
    loading: allNetworksLoading,
    error: allNetworksError,
    refetch: refetchAllNetworks,
  } = useNetworks({
    skip: network === null || network.children === 0,
  });

  const childNetworks = useMemo<Network[]>(() => {
    if (network === null || network.children === 0 || allNetworks.length === 0) {
      return [];
    }
    const rows = buildNetworkTree(allNetworks);
    return rows.filter((row) => row.parentId === network.id).map((row) => row.network);
  }, [network, allNetworks]);

  const [activeTabKey, setActiveTabKey] = useState<string | number>('overview');
  const [comingSoonAction, setComingSoonAction] = useState<string | null>(null);

  useEffect(() => {
    if (network === null) return;
    if (location.hash === '#ips' && network.children === 0) {
      setActiveTabKey('ips');
    } else if (location.hash === '#ips' && network.children > 0) {
      setActiveTabKey('overview');
    }
  }, [network, location.hash]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const showEditButton = idIsValid && !loading && !error && network !== null;
  const titleText = network?.prefix ?? 'Network';
  const breadcrumbLeaf = idIsValid ? (network?.prefix ?? (loading ? '…' : 'Network')) : 'Not found';

  const header = (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to="/datacenter/networks">Networks</BreadcrumbItem>
          <BreadcrumbItem isActive>{breadcrumbLeaf}</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <NetworkIcon />
              </Icon>
              {titleText}
            </Content>
            <Content component="p">IP network prefix details</Content>
          </FlexItem>
          {showEditButton && (
            <FlexItem>
              <Button variant="primary" icon={<PencilAltIcon />} onClick={() => setComingSoonAction('Edit network')}>
                Edit
              </Button>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
    </>
  );

  const renderContent = () => {
    if (!idIsValid) {
      return (
        <EmptyState titleText="Network not found" icon={ExclamationCircleIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>The requested network does not exist.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" component={(props) => <Link {...props} to="/datacenter/networks" />}>
                Back to networks
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (loading) {
      return (
        <Bullseye>
          <Spinner size="xl" aria-label="Loading network" />
        </Bullseye>
      );
    }

    if (error) {
      return (
        <Alert
          variant="danger"
          isInline
          title="Failed to load network"
          actionLinks={<AlertActionLink onClick={() => void refetch()}>Retry</AlertActionLink>}
        >
          {error}
        </Alert>
      );
    }

    if (network === null) {
      return (
        <EmptyState titleText="Network not found" icon={ExclamationCircleIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>The requested network does not exist.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" component={(props) => <Link {...props} to="/datacenter/networks" />}>
                Back to networks
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    const isContainer = network.children > 0;

    return (
      <Tabs
        activeKey={activeTabKey}
        onSelect={(_event, eventKey) => setActiveTabKey(eventKey)}
        isBox
        aria-label="Network detail sections"
      >
        <Tab eventKey="overview" title={<TabTitleText>Overview</TabTitleText>}>
          <Stack hasGutter>
            <StackItem>
              <Card>
                <CardTitle>Prefix</CardTitle>
                <CardBody>
                  <DescriptionList columnModifier={{ default: '1Col', sm: '2Col', lg: '3Col' }}>
                    <DescriptionListGroup>
                      <DescriptionListTerm>Prefix</DescriptionListTerm>
                      <DescriptionListDescription>
                        <code>{network.prefix}</code>
                      </DescriptionListDescription>
                    </DescriptionListGroup>
                    <DescriptionListGroup>
                      <DescriptionListTerm>Family</DescriptionListTerm>
                      <DescriptionListDescription>{renderFamilyLabel(network.family)}</DescriptionListDescription>
                    </DescriptionListGroup>
                    <DescriptionListGroup>
                      <DescriptionListTerm>Status</DescriptionListTerm>
                      <DescriptionListDescription>{renderStatusLabel(network.status)}</DescriptionListDescription>
                    </DescriptionListGroup>
                    <DescriptionListGroup>
                      <DescriptionListTerm>VLAN</DescriptionListTerm>
                      <DescriptionListDescription>{renderVlan(network.vlan)}</DescriptionListDescription>
                    </DescriptionListGroup>
                    <DescriptionListGroup>
                      <DescriptionListTerm>Site</DescriptionListTerm>
                      <DescriptionListDescription>{renderSite(network.site)}</DescriptionListDescription>
                    </DescriptionListGroup>
                    <DescriptionListGroup>
                      <DescriptionListTerm>NetBox ID</DescriptionListTerm>
                      <DescriptionListDescription>{network.id}</DescriptionListDescription>
                    </DescriptionListGroup>
                    <DescriptionListGroup>
                      <DescriptionListTerm>Description</DescriptionListTerm>
                      <DescriptionListDescription>{renderDescription(network.description)}</DescriptionListDescription>
                    </DescriptionListGroup>
                  </DescriptionList>
                </CardBody>
              </Card>
            </StackItem>
            {isContainer && (
              <StackItem>
                <Card>
                  <CardTitle>{`Child networks (${network.children})`}</CardTitle>
                  <CardBody>
                    {allNetworksLoading ? (
                      <Bullseye>
                        <Spinner size="xl" aria-label="Loading child networks" />
                      </Bullseye>
                    ) : allNetworksError ? (
                      <Alert
                        variant="danger"
                        isInline
                        title="Failed to load child networks"
                        actionLinks={<AlertActionLink onClick={() => void refetchAllNetworks()}>Retry</AlertActionLink>}
                      >
                        {allNetworksError}
                      </Alert>
                    ) : childNetworks.length === 0 ? (
                      <EmptyState titleText="No child networks found" icon={NetworkIcon} headingLevel="h3">
                        <EmptyStateBody>
                          This prefix is marked as a container but no direct child prefixes were returned for your
                          organization.
                        </EmptyStateBody>
                      </EmptyState>
                    ) : (
                      <Table aria-label={`Child networks of ${network.prefix}`} variant="compact">
                        <Thead>
                          <Tr>
                            <Th>Prefix</Th>
                            <Th>Family</Th>
                            <Th>VLAN</Th>
                            <Th>Status</Th>
                            <Th>Description</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {childNetworks.map((child) => (
                            <Tr key={child.id}>
                              <Td dataLabel="Prefix">
                                <Link to={`/datacenter/networks/${child.id}`}>{child.prefix}</Link>
                              </Td>
                              <Td dataLabel="Family">{renderFamilyLabel(child.family)}</Td>
                              <Td dataLabel="VLAN">{renderVlan(child.vlan)}</Td>
                              <Td dataLabel="Status">{renderStatusLabel(child.status)}</Td>
                              <Td dataLabel="Description">{renderDescription(child.description)}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    )}
                  </CardBody>
                </Card>
              </StackItem>
            )}
          </Stack>
        </Tab>
        {!isContainer && (
          <Tab eventKey="ips" title={<TabTitleText>IP addresses</TabTitleText>}>
            {addressesLoading ? (
              <Bullseye>
                <Spinner size="xl" aria-label="Loading IP addresses" />
              </Bullseye>
            ) : addressesError ? (
              <Alert
                variant="danger"
                isInline
                title="Failed to load IP addresses"
                actionLinks={<AlertActionLink onClick={() => void refetchAddresses()}>Retry</AlertActionLink>}
              >
                {addressesError}
              </Alert>
            ) : addresses.length === 0 ? (
              <EmptyState titleText="No IP addresses" icon={NetworkIcon} variant="lg" headingLevel="h2">
                <EmptyStateBody>This subnet has no recorded IP addresses in NetBox.</EmptyStateBody>
              </EmptyState>
            ) : (
              <Table aria-label={`IP addresses for ${network.prefix}`} variant="compact">
                <Thead>
                  <Tr>
                    <Th>Address</Th>
                    <Th>Family</Th>
                    <Th>Status</Th>
                    <Th>Role</Th>
                    <Th>DNS name</Th>
                    <Th>Description</Th>
                    <Th>Assigned to</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {addresses.map((addr) => (
                    <Tr key={addr.id}>
                      <Td dataLabel="Address">
                        <code>{addr.address}</code>
                      </Td>
                      <Td dataLabel="Family">{renderFamilyLabel(addr.family)}</Td>
                      <Td dataLabel="Status">{renderAddressStatusLabel(addr.status)}</Td>
                      <Td dataLabel="Role">{renderAddressRole(addr.role)}</Td>
                      <Td dataLabel="DNS name">{renderPlainText(addr.dns_name)}</Td>
                      <Td dataLabel="Description">{renderPlainText(addr.description)}</Td>
                      <Td dataLabel="Assigned to">{renderAddressAssigned(addr.assigned)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </Tab>
        )}
      </Tabs>
    );
  };

  return (
    <>
      {header}
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>
      <NetworkActionComingSoonModal actionLabel={comingSoonAction} onClose={() => setComingSoonAction(null)} />
    </>
  );
}

export default NetworkDetail;
